import React from "react";
import DecisionPenLogo from "../../../components/DecisionPenLogo";
import { v2cx } from "../../theme/tokens";
import { useMobileChrome } from "../../mobile/MobileChromeContext";

/**
 * Mobile-only route-aware context bar.
 * Fixed height so tab switches (and subtitle on/off) do not jump the layout.
 */
export default function MobileContextHeader({
  title,
  subtitle,
  onBack,
  backLabel,
  trailing,
  showLogo = false,
}) {
  const chrome = useMobileChrome();

  const resolvedTitle = title || chrome.contextTitle || "DecisionPen";
  const resolvedSubtitle = subtitle ?? chrome.contextSubtitle;
  const resolvedOnBack = onBack ?? chrome.onBack;
  const resolvedBackLabel = backLabel || chrome.backLabel || "Back";
  const resolvedTrailing = trailing ?? chrome.headerTrailing;

  return (
    <div
      className={v2cx(
        "lg:hidden shrink-0 border-b border-slate-200 bg-white/90 pt-[env(safe-area-inset-top,0px)] backdrop-blur supports-[backdrop-filter]:bg-white/80"
      )}
    >
      <div className="flex h-14 items-center gap-2 overflow-hidden px-3">
        {resolvedOnBack ? (
          <button
            type="button"
            onClick={resolvedOnBack}
            className="inline-flex h-10 max-w-[6.5rem] shrink-0 items-center justify-center gap-0.5 rounded-xl text-sm font-medium text-blue-700 active:bg-slate-100"
            aria-label={resolvedBackLabel}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-5 w-5 shrink-0"
              aria-hidden
            >
              <path
                fillRule="evenodd"
                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            <span className="truncate pr-1 text-xs">{resolvedBackLabel}</span>
          </button>
        ) : showLogo ? (
          <div className="flex h-10 w-8 shrink-0 items-center justify-center">
            <DecisionPenLogo size={26} />
          </div>
        ) : (
          <div className="h-10 w-0 shrink-0" aria-hidden />
        )}

        <div className="min-w-0 flex-1">
          <h1 className="truncate text-base font-semibold leading-5 tracking-tight text-slate-900">
            {resolvedTitle}
          </h1>
          {resolvedSubtitle ? (
            <p className="truncate text-xs leading-4 text-slate-500">
              {resolvedSubtitle}
            </p>
          ) : null}
        </div>

        {resolvedTrailing ? (
          <div className="flex h-10 shrink-0 items-center gap-1.5 [&_button]:h-10 [&_button]:min-h-0">
            {resolvedTrailing}
          </div>
        ) : (
          <div className="h-10 w-0 shrink-0" aria-hidden />
        )}
      </div>
    </div>
  );
}
